import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Don't show on admin pages
    if (window.location.pathname.startsWith('/admin')) return;
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences and initialize analytics if accepted
      const savedPrefs = JSON.parse(consent);
      setPreferences(savedPrefs);
      if (savedPrefs.analytics) {
        initializeAnalytics();
      }
    }
  }, []);

  const initializeAnalytics = () => {
    // Google Analytics 4 initialization
    const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (!GA_ID || GA_ID === 'G-XXXXXXXXXX') {
      console.log('Google Analytics ID not configured');
      return;
    }

    // Load gtag script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    script.async = true;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', GA_ID, {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure'
    });
  };

  const handleAcceptAll = () => {
    const newPrefs = { necessary: true, analytics: true, marketing: true };
    setPreferences(newPrefs);
    localStorage.setItem('cookie_consent', JSON.stringify(newPrefs));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    initializeAnalytics();
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const newPrefs = { necessary: true, analytics: false, marketing: false };
    setPreferences(newPrefs);
    localStorage.setItem('cookie_consent', JSON.stringify(newPrefs));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent', JSON.stringify(preferences));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    if (preferences.analytics) {
      initializeAnalytics();
    }
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#1A2B3C] text-white shadow-2xl" data-testid="cookie-banner">
      <div className="max-w-7xl mx-auto px-4 py-4">
        {!showSettings ? (
          // Main Banner
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="text-[#D4AF37] flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="text-sm md:text-base">
                  We use cookies to enhance your shopping experience, analyze site traffic, and personalize content. 
                  By clicking "Accept All", you consent to our use of cookies.{' '}
                  <Link to="/cookie-policy" className="text-[#D4AF37] hover:underline">
                    Learn more
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowSettings(true)}
                className="px-4 py-2 text-sm border border-white/30 hover:bg-white/10 transition-colors"
                data-testid="cookie-settings-btn"
              >
                Cookie Settings
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm border border-white/30 hover:bg-white/10 transition-colors"
                data-testid="cookie-reject-btn"
              >
                Reject All
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-2 text-sm bg-[#D4AF37] text-[#1A2B3C] font-semibold hover:bg-[#C4A030] transition-colors"
                data-testid="cookie-accept-btn"
              >
                Accept All
              </button>
            </div>
          </div>
        ) : (
          // Settings Panel
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Cookie className="text-[#D4AF37]" size={20} />
                Cookie Preferences
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-1 hover:bg-white/10 rounded"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="grid gap-3">
              {/* Necessary Cookies */}
              <div className="flex items-center justify-between p-3 bg-white/5 rounded">
                <div>
                  <p className="font-medium">Necessary Cookies</p>
                  <p className="text-sm text-white/60">Required for the website to function. Cannot be disabled.</p>
                </div>
                <div className="bg-[#D4AF37] text-[#1A2B3C] text-xs font-semibold px-2 py-1 rounded">
                  Always Active
                </div>
              </div>
              
              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-3 bg-white/5 rounded">
                <div>
                  <p className="font-medium">Analytics Cookies</p>
                  <p className="text-sm text-white/60">Help us understand how visitors interact with our website.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4AF37]"></div>
                </label>
              </div>
              
              {/* Marketing Cookies */}
              <div className="flex items-center justify-between p-3 bg-white/5 rounded">
                <div>
                  <p className="font-medium">Marketing Cookies</p>
                  <p className="text-sm text-white/60">Used to deliver personalized advertisements.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4AF37]"></div>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm border border-white/30 hover:bg-white/10 transition-colors"
              >
                Reject All
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-6 py-2 text-sm bg-[#D4AF37] text-[#1A2B3C] font-semibold hover:bg-[#C4A030] transition-colors"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
